import { TYPED_SIGNATURE_STYLES, REQUIRED_TEXT_BLOCKS } from './constants';

// Generate typed signature templates
export const generateTypedSignatureTemplates = (name) => {
  if (typeof window === 'undefined' || !name?.trim()) return [];
  const trimmed = name.trim();
  return TYPED_SIGNATURE_STYLES.map((style) => {
    const baseWidth = 1400;
    const fontSize = style.fontSize || 64;
    const height = Math.ceil(fontSize);
    const measurer = document.createElement('canvas').getContext('2d');
    if (!measurer) return null;
    measurer.font = `${fontSize}px ${style.fontFamily}`;
    const measuredWidth = measurer.measureText(trimmed).width;
    const width = Math.max(baseWidth, Math.ceil(measuredWidth));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `${fontSize}px ${style.fontFamily}`;
    ctx.fillText(trimmed, 0, 0);
    return {
      ...style,
      image: canvas.toDataURL('image/png'),
    };
  }).filter(Boolean);
};

// Helper functions for placement
export const normalizePlacement = (placement) => {
  if (!placement || typeof placement !== 'object') return null;
  const pageNumber = Number(placement.pageNumber);
  const xPct = Number(placement.xPct);
  const yPct = Number(placement.yPct);
  if (Number.isNaN(pageNumber) || Number.isNaN(xPct) || Number.isNaN(yPct)) {
    return null;
  }
  const widthPct = typeof placement.widthPct === 'number' ? placement.widthPct : Number(placement.widthPct);

  return {
    pageNumber,
    xPct,
    yPct,
    widthPct: Number.isNaN(widthPct) ? undefined : widthPct,
    canvasXPct: placement.canvasXPct,
    canvasYPct: placement.canvasYPct,
    pdfX: placement.pdfX,
    pdfY: placement.pdfY,
    pdfWidth: placement.pdfWidth,
    pdfHeight: placement.pdfHeight,
    canvasRect: placement.canvasRect,
    wrapperRect: placement.wrapperRect,
  };
};

export const getBlockPlacementEntries = (block) => {
  if (!block) return [];
  const normalized = Array.isArray(block.placements) && block.placements.length
    ? block.placements
        .map((entry) => {
          if (entry?.placement) {
            const normalizedPlacement = normalizePlacement(entry.placement);
            if (!normalizedPlacement) return null;
            return {
              placement: normalizedPlacement,
              value: entry.value ?? entry.text ?? block.value ?? entry.placement?.value ?? null,
            };
          }
          const normalizedPlacement = normalizePlacement(entry);
          if (!normalizedPlacement) return null;
          return {
            placement: normalizedPlacement,
            value: block.value ?? null,
          };
        })
        .filter((entry) => entry && entry.placement)
    : [];
  if (normalized.length) {
    return normalized;
  }
  const legacy = normalizePlacement(block.placement);
  if (!legacy) return [];
  return [
    {
      placement: legacy,
      value: block.value ?? null,
    },
  ];
};

export const blockHasPlacement = (block) => getBlockPlacementEntries(block).length > 0;

export const supportsMultiplePlacements = (blockOrId) => {
  if (typeof blockOrId === 'object' && blockOrId !== null) {
    // Check if block has allowMultiplePlacements set
    if (blockOrId.allowMultiplePlacements === true) {
      return true;
    }
    // Check if it's an initial type (initials typically need multiple placements)
    if (blockOrId.type === 'initial') {
      return true;
    }
  }
  const id = typeof blockOrId === 'string' ? blockOrId : blockOrId?.id;
  return Boolean(id && REQUIRED_TEXT_BLOCKS.find((b) => b.id === id)?.allowMultiplePlacements);
};

export const createDefaultTextBlocks = () =>
  REQUIRED_TEXT_BLOCKS.map((block) => ({
    ...block,
    value: block.type === 'date' ? null : '',
    placement: null,
    placements: [],
    allowMultiplePlacements: block.allowMultiplePlacements ?? false,
    captureValuePerPlacement: block.captureValuePerPlacement ?? false,
    required: true,
  }));

export const textBlockHasValue = (block) => {
  if (!block) return false;
  if (block.type === 'date') return Boolean(block.value);
  return Boolean(block.value && String(block.value).trim());
};

export const collectTextOverlaysForPage = (blocks = [], pageNumber) =>
  blocks.flatMap((block) => {
    const entries = getBlockPlacementEntries(block).filter(
      (entry) => entry?.placement?.pageNumber === pageNumber
    );
    return entries
      .map((entry, index) => {
        const textValue = block.captureValuePerPlacement ? entry.value : block.value;
        if (
          (block.type === 'date' && !textValue) ||
          (block.type !== 'date' && (!textValue || !String(textValue).trim()))
        ) {
          return null;
        }
        return {
          id: `${block.id}-${index}`,
          placement: entry.placement,
          text: textValue,
          type: block.type,
          widthPct: block.widthPct,
        };
      })
      .filter(Boolean);
  });

export const formatSignDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
